import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { SyntheticEvent, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { AddNewBingoUser } from "src/components/bingo/AddNewBingoUser";
import {
  createBingoResolutionsForNewUser,
  CreateBingoResolutionsForNewUserReqyest,
  fetchValidBingoUsers,
} from "src/components/bingo/api";
import BingoContainer from "src/components/bingo/BingoContainer";
import { a11yProps, CustomTabPanel } from "src/components/tabs/CustomTabPanel";
import { Severity, SnackbarContext } from "src/contexts/SnackbarContext";

export function YearBingoPage() {
  const { showSnackbar } = useContext(SnackbarContext);
  let navigate = useNavigate();
  let { year, user } = useParams();
  if (!year) {
    year = new Date().getFullYear().toString();
  }
  const [value, setValue] = useState(0); // Default to first tab

  const { data: validUsers, refetch: refetchValidUsers } = useQuery({
    queryKey: ["fetchValidBingoUsers", year],
    queryFn: async () => {
      if (!year) return [];
      return fetchValidBingoUsers(year).then((res) => res.data || []);
    },
    enabled: !!year, // Only run the query if year is defined
  });
  const newTabIndex = validUsers ? validUsers.length : 0;

  useEffect(() => {
    if (user === "new") {
      setValue(newTabIndex);
    } else if (validUsers && user) {
      setValue(validUsers.indexOf(user));
    }
  }, [validUsers, user, newTabIndex]);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    // Update the tab
    setValue(newValue);

    // Update the pathname so it looks like /bingo/<year>/<user> without navigating.
    // We use replaceState so the browser URL updates but React Router does not perform a navigation.
    const urlUser =
      validUsers && newValue >= 0 && newValue < validUsers.length
        ? validUsers[newValue]
        : "new";
    const urlPostfix = `/bingo/${year}/${urlUser}`;
    try {
      const url = new URL(window.location.href);
      url.pathname = urlPostfix;
      window.history.replaceState(null, "", url.toString());
    } catch (e) {
      // Fallback: replace just the path portion
      window.history.replaceState(null, "", urlPostfix);
    }
  };

  const createBingoResolutionsForNewUserMutation = useMutation({
    mutationFn: (mutationData: CreateBingoResolutionsForNewUserReqyest) => {
      return createBingoResolutionsForNewUser(mutationData);
    },
  });

  async function handleUserAdded(newUser: string) {
    if (!year) return;
    const createBingoResolutionsForNewUserRequest: CreateBingoResolutionsForNewUserReqyest =
      {
        user: newUser,
        year: year,
      };

    await createBingoResolutionsForNewUserMutation.mutateAsync(
      createBingoResolutionsForNewUserRequest,
      {
        onSuccess: async (data) => {},
        onError: (data) => {
          showSnackbar({
            message: "Error creating bingo resolutions for new user",
            severity: Severity.ERROR,
          });
          console.error("Error creating bingo resolutions for new user:", data);
        },
      },
    );

    refetchValidUsers();
    navigate(`/bingo/${year}/${newUser}`);
  }

  const theme = createTheme({
    typography: {
      fontFamily: [
        "Patrick Hand", // Your desired font
        "Dancing Script", // Fallback font
        "cursive",
      ].join(","),
      // You can also change the default font size here
      // fontSize: 12,
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{ fontFamily: `"Patrick Hand", "Dancing Script", "cursive"` }}
        className="flex flex-col gap-2"
      >
        <Box className={"align-center w-full text-center text-4xl pt-4"}>
          Year Bingo {year}
        </Box>

        {validUsers === undefined ? (
          <div className="flex m-4">
            <CircularProgress />
          </div>
        ) : (
          <>
            <Box
              sx={{ borderBottom: 1, borderColor: "divider", paddingTop: 2 }}
            >
              <Tabs
                variant="scrollable"
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                {validUsers?.map((validUser, index) => (
                  <Tab
                    key={validUser}
                    label={validUser}
                    {...a11yProps(index)}
                    sx={{
                      fontSize: 24,
                    }}
                  />
                ))}
                <Tab
                  key="new"
                  label="New"
                  {...a11yProps(newTabIndex)}
                  sx={{
                    fontSize: 24,
                  }}
                />
              </Tabs>
            </Box>
            {validUsers &&
              validUsers?.map((validUser, index) => (
                <CustomTabPanel value={value} index={index} key={validUser}>
                  <BingoContainer year={year!} user={validUser} />
                </CustomTabPanel>
              ))}
            <CustomTabPanel value={value} index={newTabIndex}>
              <AddNewBingoUser
                otherUsers={validUsers || []}
                onUserAdded={(newUser) => handleUserAdded(newUser)}
              />
            </CustomTabPanel>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}
